import type { ComponentPropsWithoutRef, HTMLAttributes } from 'react';
import { createContext, useContext, useState } from 'react';

import { cn } from '../../lib/utils';
import { useHorizontalScrollState } from '../useHorizontalScrollState';

interface TovSegmentedControlContextValue {
  value: string;
  setValue(nextValue: string): void;
}

const TovSegmentedControlContext =
  createContext<TovSegmentedControlContextValue | null>(null);

export interface TovSegmentedControlProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'defaultValue'
> {
  defaultValue?: string;
  onValueChange?(value: string): void;
  value?: string;
}

export function TovSegmentedControl({
  children,
  className,
  defaultValue = '',
  onScroll,
  onValueChange,
  value,
  ...props
}: TovSegmentedControlProps) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const currentValue = value ?? internalValue;
  const { canScrollLeft, canScrollRight, ref, updateScrollState } =
    useHorizontalScrollState<HTMLDivElement>();

  const setValue = (nextValue: string) => {
    if (value === undefined) {
      setInternalValue(nextValue);
    }

    onValueChange?.(nextValue);
  };

  return (
    <TovSegmentedControlContext.Provider
      value={{ value: currentValue, setValue }}
    >
      <div
        className={cn('tov-segmented', className)}
        data-can-scroll-left={canScrollLeft ? 'true' : undefined}
        data-can-scroll-right={canScrollRight ? 'true' : undefined}
        onScroll={(event) => {
          onScroll?.(event);
          updateScrollState();
        }}
        ref={ref}
        {...props}
      >
        {children}
      </div>
    </TovSegmentedControlContext.Provider>
  );
}

export interface TovSegmentedControlItemProps extends Omit<
  ComponentPropsWithoutRef<'button'>,
  'value'
> {
  value: string;
}

export function TovSegmentedControlItem({
  className,
  onClick,
  value,
  ...props
}: TovSegmentedControlItemProps) {
  const context = useContext(TovSegmentedControlContext);
  const active = context?.value === value;

  return (
    <button
      className={cn('tov-segmented__item', className)}
      data-state={active ? 'active' : 'inactive'}
      onClick={(event) => {
        context?.setValue(value);
        onClick?.(event);
      }}
      type="button"
      {...props}
    />
  );
}

export const SegmentedControl = TovSegmentedControl;
export const SegmentedControlItem = TovSegmentedControlItem;
